import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import OfficeLayout from '../../components/OfficeLayout';
import { StatusBadge } from '../../components/StatusBadge';
import { getArchive } from '../../utils/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { Search } from 'lucide-react';

export default function ArchivePage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const params = {};
      if (typeFilter) params.document_type = typeFilter;
      if (search) params.search = search;
      const { data } = await getArchive(params);
      setRecords(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [typeFilter, search]);

  useAutoRefresh(load, 20000);

  useEffect(() => { load(); }, [load]);

  const ref = (r) => r.document_type === 'trip_sheet' ? r.trip_sheet_fields?.trip_sheet_no || '—' : r.waybill_fields?.waybill_no || '—';

  return (
    <OfficeLayout>
      <div data-testid="archive-page">
        <h2 className="text-2xl font-bold tracking-tight text-[#1A1A2E] mb-6">Archive</h2>

        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
          <form onSubmit={e => { e.preventDefault(); load(); }} className="flex gap-2 flex-1 min-w-[200px]">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search archived records..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]" data-testid="archive-search-input" />
            </div>
            <button type="submit" className="px-4 py-2 bg-[#0D9488] text-white rounded-lg text-sm font-medium" data-testid="archive-search-button">Search</button>
          </form>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="archive-type-filter">
            <option value="">All Types</option>
            <option value="trip_sheet">Trip Sheet</option>
            <option value="waybill">Waybill</option>
          </select>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div></div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Ref No.</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Type</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Agent</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Driver</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Completed</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 ? (
                  <tr><td colSpan="7" className="px-5 py-12 text-center text-[#6B7280]">No archived records found.</td></tr>
                ) : records.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-5 py-3 font-semibold text-[#1A1A2E]">{ref(r)}</td>
                    <td className="px-5 py-3 capitalize text-[#6B7280]">{r.document_type.replace('_', ' ')}</td>
                    <td className="px-5 py-3 text-[#1A1A2E]">{r.third_party_agent}</td>
                    <td className="px-5 py-3 text-[#6B7280]">{r.allocated_driver_name || '—'}</td>
                    <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-5 py-3 text-[#6B7280]">{r.completed_at ? new Date(r.completed_at).toLocaleDateString() : '—'}</td>
                    <td className="px-5 py-3"><button onClick={() => navigate(`/record/${r.id}`)} className="text-[#0D9488] hover:text-[#0F766E] font-medium text-sm">View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </OfficeLayout>
  );
}
